import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MainLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [locaties, setLocaties] = useState([]);
  const [selectedLocatie, setSelectedLocatie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  // Separate flag so we can distinguish "still loading" from "loaded but no
  // accessible locations" — admin without locatie assignment previously saw
  // an eternal spinner; now we surface a clear message (v18.1.5 bug-fix).
  const [accessibleCount, setAccessibleCount] = useState(null);

  const { user, isAdmin, hasLocationAccess, logout } = useAuth();

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      // Auto-open sidebar on desktop
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load locaties on mount
  useEffect(() => {
    const fetchLocaties = async () => {
      try {
        const response = await axios.get(`${API}/locaties`);
        const allLocaties = response.data;
        setLocaties(allLocaties);
        
        // Filter accessible locations for this user
        const accessibleLocaties = isAdmin() 
          ? allLocaties 
          : allLocaties.filter(loc => hasLocationAccess(loc._id));
        setAccessibleCount(accessibleLocaties.length);
        
        // Check localStorage for saved location
        const savedLocatieId = localStorage.getItem('selectedLocatieId');
        
        // Verify the saved location is accessible to this user
        if (savedLocatieId && accessibleLocaties.find(l => l._id === savedLocatieId)) {
          setSelectedLocatie(savedLocatieId);
        } else if (accessibleLocaties.length > 0) {
          // Default to first accessible location
          setSelectedLocatie(accessibleLocaties[0]._id);
          localStorage.setItem('selectedLocatieId', accessibleLocaties[0]._id);
        }
      } catch (error) {
        console.error('Error fetching locaties:', error);
        setAccessibleCount(0);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchLocaties();
    }
  }, [user, isAdmin, hasLocationAccess]);

  const handleLocatieChange = (locatieId) => {
    setSelectedLocatie(locatieId);
    localStorage.setItem('selectedLocatieId', locatieId);
    // Close sidebar on mobile after selection
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleCloseSidebar = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const selectedLocatieData = locaties.find(l => l._id === selectedLocatie);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Laden...</p>
        </div>
      </div>
    );
  }

  // v18.1.5 — Admin without assigned locations previously saw a spinner
  // forever because child pages returned early on `!selectedLocatie`.
  // Now we surface a clear full-screen message so the user knows to
  // contact a sysadmin instead of being stuck in loading limbo.
  if (accessibleCount === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-100 p-4" data-testid="geen-locaties-melding">
        <div className="max-w-md text-center space-y-4">
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="w-7 h-7 text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-[#1f2548]">Geen locaties toegewezen</h1>
          <p className="text-sm text-slate-600 leading-relaxed">
            Je account heeft nog geen locaties toegewezen. Neem contact op met
            een systeembeheerder om toegang te krijgen tot je werkgebied.
          </p>
          <p className="text-xs text-slate-400 pt-1">Ingelogd als {user?.email || ''}</p>
          <button
            onClick={logout}
            className="mt-4 px-4 py-2 text-sm bg-[#1f2548] hover:bg-[#2a3060] text-white rounded-md"
            data-testid="geen-locaties-logout-btn"
          >
            Uitloggen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-100">
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
          onClick={handleCloseSidebar}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        ${isMobile ? 'fixed inset-y-0 left-0 z-50' : 'relative'}
        transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${!isMobile && !sidebarOpen ? 'w-0' : ''}
      `}>
        <Sidebar
          locaties={locaties}
          selectedLocatie={selectedLocatie}
          onLocatieChange={handleLocatieChange}
          isOpen={sidebarOpen}
          onClose={handleCloseSidebar}
          isMobile={isMobile}
        />
      </div>
      
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <Header
          onToggleSidebar={handleToggleSidebar}
          locatieNaam={selectedLocatieData?.naam}
          sidebarOpen={sidebarOpen}
        />
        
        <main className="flex-1 overflow-y-auto p-4 lg:p-6 pb-safe">
          <Outlet context={{ selectedLocatie, selectedLocatieData, locaties }} />
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
